import { useEffect, useRef } from 'react';

import type { AssignWizardHandle, CustomTestDetail, CustomTestLanguage, QuestionTree, QuestionTreeResult } from 'pages/custom_tests_v1';
import { CustomTestEditContext } from 'pages/custom_tests_v1/components/custom_test_edit/context';
import { AssignWizard } from 'pages/custom_tests_v1/components/assign_wizard/AssignWizard';
import * as Tabs from 'pages/custom_tests_v1/components/custom_test_edit/tabs';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { CustomTestApi } from 'apis/CustomTestApi';
import { LanguagesApi } from 'apis/LanguagesApi';
import { Link } from 'react-router-dom';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

enum ETabs {
    SETTINGS,
    QUESTIONS,
    ASSIGN
}

enum EMode {
    TRAINING = 1,
    TESTING
}

export type CustomTestEditContextValue = {
    name: string;
    categoryId: number;
    handleQuestionsChange: (questionIds: string, totalSelected: number) => void;
    handleSave: () => Promise<void>;
    hasResults: boolean;
    isEditMode?: boolean;
    isOwner: boolean;
    languageId?: number;
    languages?: { Id: number; Name: string }[];
    loading: boolean;
    loadingQuestions: boolean;
    minScore: number;
    minutes: number;
    modeId: EMode;
    nameValidationError?: string;
    productIds: number[];
    questionTree: QuestionTree;
    randomly: number;
    saving: boolean;
    selectedQuestionIds: string;
    setProviderState: (state: any) => void;
    testId: number;
    totalQuestions?: number;
    useRandomly: boolean;
};

export const CustomTestEdit = () => {

    const [state, setState] = useSuperState({
        name: '',
        activeTab: ETabs.SETTINGS,
        categoryId: 0,
        createdBy: '',
        hasResults: false,
        isActive: true,
        languageId: 0,
        languages: [] as CustomTestLanguage[],
        loading: true,
        loadingQuestions: false,
        minScore: 70,
        minutes: 60,
        modeId: EMode.TESTING as EMode,
        nameValidationError: '',
        productIds: [] as number[],
        questionIds: '',
        questionsXml: '',
        questionTree: { Products: [] } as QuestionTree,
        randomly: 0,
        saving: false,
        selectedQuestionIds: '',
        testId: 0,
        totalQuestions: 0,
        useRandomly: false
    });

    const { name, activeTab, categoryId, createdBy, hasResults, loading, loadingQuestions, minScore, minutes, modeId, nameValidationError,
        productIds, questionTree, randomly, saving, selectedQuestionIds, testId, useRandomly } = state;

    const { currentUser } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const assignWizardRef = useRef<AssignWizardHandle>(null);

    // CreatedBy is the creator's "First Last" name; blank means no known owner, so anyone may edit.
    const creator = createdBy.trim();
    const isOwner = !creator || creator === currentUser?.fullName?.trim();

    const setActiveTab = (activeTab: ETabs) => setState({ activeTab });

    const fetchTestData = async (id: number) => {

        try {

            setState({ loading: true });

            const [test, languages] = await Promise.all([
                CustomTestApi.Get(id),
                LanguagesApi.GetAll()
            ]) as [CustomTestDetail, CustomTestLanguage[]];

            setState({
                name: test?.Name,
                categoryId: test?.CategoryId,
                createdBy: test?.CreatedBy ?? '',
                hasResults: test?.HasResults,
                isActive: test?.IsActive,
                languageId: languages[0]?.Id || 0,
                languages,
                loading: false,
                minScore: test?.MinScore,
                minutes: test?.Minutes,
                modeId: test?.ModeId,
                productIds: test?.ProductIds ?? [],
                questionIds: test?.SelectedQuestionIds ?? '',
                questionsXml: test?.QuestionsXml ?? '',
                randomly: test?.Randomly,
                selectedQuestionIds: test?.SelectedQuestionIds ?? '',
                totalQuestions: test?.NumQuestions,
                useRandomly: test?.UseRandomly
            });

        } catch (e: any) {

            setState({ loading: false });

            createToast(
                e?.message ?? (formatMessage({ id: 'failed_to_load_test_data.6fe1536e', defaultMessage: 'Failed to load test data.' })),
                'danger'
            );

        }

    };

    const fetchQuestions = async () => {

        if (!state?.productIds?.length) return;

        try {

            setState({ loadingQuestions: true });

            const productIdsStr = state.productIds.join(',');
            const result: QuestionTreeResult = await CustomTestApi.GetQuestions(
                productIdsStr,
                state.categoryId,
                state.languageId,
                state.modeId,
                state.selectedQuestionIds
            );

            setState({
                loadingQuestions: false,
                questionsXml: result.XmlString,
                questionTree: result
            });

        } catch (e: any) {

            setState({ loadingQuestions: false });
            createToast(
                e?.message ?? (formatMessage({ id: 'failed_to_load_questions.82bf5a85', defaultMessage: 'Failed to load questions.' })),
                'danger'
            );

        }

    };

    const handleQuestionsChange = (questionIds: string, totalSelected: number) => {

        setState({ questionIds, totalQuestions: totalSelected });

    };

    const handleSave = async () => {

        if (!state.name.trim()) {

            setState({ nameValidationError: formatMessage({ id: 'test_name_is_required.88a8be5d', defaultMessage: 'Test name is required.' }) });
            return;

        }

        try {

            setState({ nameValidationError: '', saving: true });

            const { Done, Message } = await CustomTestApi.Update({
                isActive: state.isActive,
                minScore: state.minScore,
                minutes: state.minutes,
                modeId: state.modeId,
                multiProductTest: state.productIds.length > 1,
                productId: state.productIds.join(','),
                questionIds: state.questionIds,
                questionsXml: state.questionsXml,
                randomly: state.randomly,
                testId: state.testId,
                testName: state.name,
                totalQuestions: state.totalQuestions,
                useRandomly: state.useRandomly
            });

            if (Done) {

                createToast(
                    formatMessage({ id: 'test_updated_successfully.1d9dd27d', defaultMessage: 'Test updated successfully.' }),
                    'success',
                    5000
                );

            } else {

                createToast(
                    Message ?? (formatMessage({ id: 'failed_to_update_test.62059e2f', defaultMessage: 'Failed to update test.' })),
                    'danger'
                );

            }

        } catch (e: any) {

            createToast(
                e?.message ?? (formatMessage({ id: 'an_error_occurred_while_saving.a141f6d8', defaultMessage: 'An error occurred while saving.' })),
                'danger'
            );

        } finally {

            setState({ saving: false });

        }

    };

    useEffect(() => {

        const params = new URLSearchParams(window.location.search);
        const testId = parseInt(params.get('testId') ?? '0', 10);

        if (testId > 0) {

            setState({ testId });
            fetchTestData(testId);

        } else {

            setState({ loading: false });

        }

    }, []);

    useEffect(() => {

        if (state?.activeTab !== ETabs.QUESTIONS || state?.questionTree?.Products?.length) return;

        fetchQuestions();

    }, [state?.activeTab, state?.questionTree]);

    const context: CustomTestEditContextValue = {
        name,
        categoryId,
        handleQuestionsChange,
        handleSave,
        hasResults,
        isOwner,
        loading,
        loadingQuestions,
        minScore,
        minutes,
        modeId,
        nameValidationError,
        productIds,
        questionTree,
        randomly,
        saving,
        selectedQuestionIds,
        setProviderState: setState,
        testId,
        useRandomly
    };

    return (
        <CustomTestEditContext.Provider value={context} >
            <div className="custom-test-edit d-flex flex-column gap-3">

                <PageWithHeader
                    headerConfig={{
                        current: name || (formatMessage({ id: 'edit_test.179c3e12', defaultMessage: 'Edit Test' })),
                        title: formatMessage({ id: 'edit_custom_test.878c4842', defaultMessage: 'Edit Custom Test' }),
                        trail: [{ label: formatMessage({ id: 'custom_tests.9e7494ea', defaultMessage: 'Custom Tests' }), to: '/custom-tests' }]
                    }}
                >

                    {loading ?
                        (
                            <div className="d-flex align-items-center justify-content-center p-5">
                                <GMetrixLoader />
                            </div>
                        )
                        :
                        (
                            <>
                                {testId === 0 ?
                                    (
                                        <div className="alert alert-warning">

                                            <FormattedMessage defaultMessage="No test selected. Please return to the list." id="no_test_selected_please_return_to_the_li.7b160c58" />

                                            <div className="mt-2">
                                                <Link className={'btn btn-primary'} to={`/custom-tests`}>
                                                    <FormattedMessage defaultMessage="Return to Custom Tests" id="return_to_custom_tests.f596c5cb" />
                                                </Link>
                                            </div>

                                        </div>
                                    )
                                    :
                                    (
                                        <>
                                            {!isOwner && (
                                                <div className="d-flex align-items-start gap-2 bg-body border rounded px-3 py-2 mb-3">
                                                    <i className="fa-solid fa-circle-info text-primary mt-1" />
                                                    <div>
                                                        <div className="fw-bold">
                                                            <FormattedMessage defaultMessage="Another user’s test" id="another_users_test.5c4b40e0" />
                                                        </div>
                                                        <div className="text-muted small">
                                                            <FormattedMessage defaultMessage="This test was created by {name}. Copy it to create an editable version of your own." id="this_test_was_created_by_name_copy_it_to.9a9e507b" values={{ name: createdBy }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <GMetrixTabs unmountOnExit className="mb-3" onSelect={setActiveTab} selected={activeTab}>

                                                <GMetrixTabsTab title={formatMessage({ id: 'settings.277e0df0', defaultMessage: 'Settings' })}>
                                                    <Tabs.Settings />
                                                </GMetrixTabsTab>

                                                <GMetrixTabsTab title={formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' })}>
                                                    <Tabs.Questions />
                                                </GMetrixTabsTab>

                                                <GMetrixTabsTab title={formatMessage({ id: 'assignments.f2dfa1bf', defaultMessage: 'Assignments' })}>
                                                    <Tabs.Assignments />
                                                </GMetrixTabsTab>

                                            </GMetrixTabs>
                                        </>
                                    )}
                            </>
                        )
                    }

                    <AssignWizard ref={assignWizardRef} />

                </PageWithHeader>

            </div>
        </CustomTestEditContext.Provider>
    );

};
